// ============================================================================
// AUTHENTICATION SERVICE
// ============================================================================
// API calls for Authentication and Authorization module.
// Supports both legacy mode and tenant-hub RBAC mode.
// Focused responsibility: API calls only — no permission checking,
// no navigation building (those live in permissionEngine.ts).

import { get, post, put, del, patch } from '../client/axiosClient';
import { authApiClient } from '../client/axiosClient';
import { authEndpoints, authorizationEndpoints, userEndpoints } from '../endpoints';
import { tokenUtils } from '../config/apiConfig';
import { tokenManager } from '../../auth/tokenManager';
import { STORAGE_KEYS } from '../../auth/storageKeys';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ListResponse,
  EmptyResponse,
  BulkOperationResponse,
  ID,
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  LogoutRequest,
  LogoutResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  UserSession,
  PermissionCheckRequest,
  PermissionCheckResponse,
  // Tenant-Hub types
  TenantHubApiResponse,
  DiscoverRequest,
  DiscoverResponse,
  AuthenticateRequest,
  AuthenticateResponse,
  UserPermissionContext,
  AccessibleUI,
  TenantHubCheckPermissionRequest,
  TenantHubCheckPermissionResponse,
  BatchPermissionCheckRequest,
  BatchPermissionCheckResponse,
} from '../types';

/**
 * Authentication Service Class
 */
class AuthService {
  // ============================================================================
  // LEGACY AUTHENTICATION METHODS (unchanged)
  // ============================================================================

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await post<LoginResponse>(authEndpoints.login(), credentials);
    const loginData = response.data;

    if (loginData.success && loginData.data) {
      tokenUtils.setToken(loginData.data.token);
      tokenUtils.setRefreshToken(loginData.data.refreshToken);
    }

    return loginData;
  }

  async logout(request?: LogoutRequest): Promise<LogoutResponse> {
    const refreshToken = request?.refreshToken || tokenUtils.getRefreshToken();
    const response = await post<LogoutResponse>(authEndpoints.logout(), {
      refreshToken,
    });

    tokenUtils.clearTokens();

    return response.data;
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await post<RegisterResponse>(authEndpoints.register(), data);
    return response.data;
  }

  async refreshToken(request?: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const refreshToken = request?.refreshToken || tokenUtils.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await post<RefreshTokenResponse>(authEndpoints.refresh(), {
      refreshToken,
    });

    const refreshData = response.data;

    if (refreshData.success && refreshData.data) {
      tokenUtils.setToken(refreshData.data.token);
      tokenUtils.setRefreshToken(refreshData.data.refreshToken);
    }

    return refreshData;
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await get<ApiResponse<User>>(authEndpoints.me());
    return response.data;
  }

  async updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<User>> {
    const response = await put<ApiResponse<User>>(authEndpoints.updateProfile(), data);
    return response.data;
  }

  async changePassword(data: ChangePasswordRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.changePassword(), data);
    return response.data;
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.forgotPassword(), data);
    return response.data;
  }

  async resetPassword(data: ResetPasswordRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.resetPassword(), data);
    return response.data;
  }

  async verifyEmail(token: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.verifyEmail(), { token });
    return response.data;
  }

  async resendVerification(email: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.resendVerification(), {
      email,
    });
    return response.data;
  }

  async checkPermission(
    request: PermissionCheckRequest
  ): Promise<ApiResponse<PermissionCheckResponse>> {
    const response = await post<ApiResponse<PermissionCheckResponse>>(
      authEndpoints.checkPermission(),
      request
    );
    return response.data;
  }

  async getSessions(): Promise<ListResponse<UserSession>> {
    const response = await get<ListResponse<UserSession>>(authEndpoints.sessions());
    return response.data;
  }

  async revokeSession(sessionId: string): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(authEndpoints.revokeSession(sessionId));
    return response.data;
  }

  isAuthenticated(): boolean {
    return tokenUtils.isAuthenticated();
  }

  getToken(): string | null {
    return tokenUtils.getToken();
  }

  // ============================================================================
  // TENANT-HUB RBAC METHODS
  // ============================================================================

  /**
   * Step 1: Discover accessible tenants and applications
   * Uses authApiClient (no Bearer token needed)
   */
  async discover(request: DiscoverRequest): Promise<TenantHubApiResponse<DiscoverResponse>> {
    const response = await authApiClient.post<TenantHubApiResponse<DiscoverResponse>>(
      authEndpoints.discover(),
      request
    );
    const data = response.data;

    // Store userId for later use
    if (data.success && data.data?.userId) {
      tokenManager.setUserId(data.data.userId);
    }

    return data;
  }

  /**
   * Step 2: Authenticate with selected tenant and application
   * Uses authApiClient (no Bearer token needed)
   * Stores all auth data via _storeAuthData
   */
  async authenticate(request: AuthenticateRequest): Promise<TenantHubApiResponse<AuthenticateResponse>> {
    const response = await authApiClient.post<TenantHubApiResponse<AuthenticateResponse>>(
      authEndpoints.authenticate(),
      request
    );
    const data = response.data;

    if (data.success && data.data) {
      this._storeAuthData(data.data, request.selectedTenantId, request.selectedApplicationId);
    }

    return data;
  }

  /**
   * Refresh access token.
   * CRS /auth/refresh proxies to TenantHub /api/v1/Auth/refresh which expects
   * the refresh token in the body along with tenant + application context.
   */
  async refreshTenantHubTokens(): Promise<{ accessToken: string; refreshToken: string }> {
    const refreshToken = tokenManager.getRefreshToken();
    const tenantId = tokenManager.getTenantId();
    const appId = tokenManager.getAppId();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const headers: Record<string, string> = {};
    if (tenantId) headers['X-Tenant-ID'] = tenantId;
    if (appId) headers['X-App-ID'] = appId;

    const response = await authApiClient.post<TenantHubApiResponse<AuthenticateResponse>>(
      authEndpoints.refresh(),
      {
        refreshToken,
        tenantId: tenantId || undefined,
        applicationId: appId || undefined,
      },
      { headers }
    );

    const data: any = response.data;
    // TenantHub response shape: { success, data: { accessToken, refreshToken, ... } }
    const tokenData = data?.data ?? data;
    const newAccessToken = tokenData?.accessToken ?? tokenData?.token;
    const newRefreshToken = tokenData?.refreshToken ?? tokenManager.getRefreshToken() ?? '';

    if (newAccessToken) {
      tokenManager.setAccessToken(newAccessToken);
      if (tokenData?.refreshToken) {
        tokenManager.setRefreshToken(tokenData.refreshToken);
      }
      return {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
      };
    }

    throw new Error(data?.message || 'Token refresh failed');
  }

  /**
   * Get user permission context from tenant-hub
   * Uses authenticated apiClient (Bearer token)
   */
  async getPermissions(applicationId: string): Promise<TenantHubApiResponse<UserPermissionContext>> {
    const response = await get<TenantHubApiResponse<UserPermissionContext>>(
      authEndpoints.permissions() + `?applicationId=${encodeURIComponent(applicationId)}`
    );
    return response.data;
  }

  /**
   * Refresh user permissions (invalidate cache)
   */
  async refreshPermissions(applicationId: string): Promise<TenantHubApiResponse<UserPermissionContext>> {
    const response = await post<TenantHubApiResponse<UserPermissionContext>>(
      authEndpoints.refreshPermissions() + `?applicationId=${encodeURIComponent(applicationId)}`
    );
    return response.data;
  }

  /**
   * Get UI details for a specific menu item (element-level permissions)
   * Used by useUIPermissions hook
   */
  async getMenuUIDetails(menuItemId: string): Promise<TenantHubApiResponse<AccessibleUI[]>> {
    const response = await get<TenantHubApiResponse<AccessibleUI[]>>(
      authEndpoints.menuUI(menuItemId)
    );
    return response.data;
  }

  /**
   * Tenant-hub authorization check (server-side)
   */
  async checkTenantHubPermission(
    request: TenantHubCheckPermissionRequest
  ): Promise<TenantHubApiResponse<TenantHubCheckPermissionResponse>> {
    const response = await post<TenantHubApiResponse<TenantHubCheckPermissionResponse>>(
      authorizationEndpoints.check(),
      request
    );
    return response.data;
  }

  /**
   * Batch permission check (server-side)
   */
  async batchCheckPermission(
    request: BatchPermissionCheckRequest
  ): Promise<TenantHubApiResponse<BatchPermissionCheckResponse>> {
    const response = await post<TenantHubApiResponse<BatchPermissionCheckResponse>>(
      authorizationEndpoints.batchCheck(),
      request
    );
    return response.data;
  }

  /**
   * Tenant-hub logout — clears all auth state
   */
  tenantHubLogout(): void {
    tokenManager.clearAll();
  }

  /**
   * Store auth data from tenant-hub authenticate response
   * SINGLE place where localStorage writes happen after auth — no duplication
   */
  _storeAuthData(data: AuthenticateResponse, tenantId: string, appId: string): void {
    // Tokens
    tokenManager.setAccessToken(data.accessToken);
    tokenManager.setRefreshToken(data.refreshToken);

    // Tenant context
    tokenManager.setTenantId(data.tenantId || tenantId);
    tokenManager.setAppId(data.applicationId || appId);
    if (data.userId) {
      tokenManager.setUserId(data.userId);
    }

    // Tenant branding (name + logo) for header display
    if (data.tenantName) localStorage.setItem(STORAGE_KEYS.TENANT_NAME, data.tenantName);
    else localStorage.removeItem(STORAGE_KEYS.TENANT_NAME);
    if (data.tenantDisplayName) localStorage.setItem(STORAGE_KEYS.TENANT_DISPLAY_NAME, data.tenantDisplayName);
    else localStorage.removeItem(STORAGE_KEYS.TENANT_DISPLAY_NAME);
    if (data.tenantLogoBase64) localStorage.setItem(STORAGE_KEYS.TENANT_LOGO, data.tenantLogoBase64);
    else localStorage.removeItem(STORAGE_KEYS.TENANT_LOGO);

    // User info
    if (data.userName) localStorage.setItem(STORAGE_KEYS.USER_NAME, data.userName);
    if (data.userEmail) localStorage.setItem(STORAGE_KEYS.USER_EMAIL, data.userEmail);
    if (data.userFirstName) localStorage.setItem(STORAGE_KEYS.USER_FIRST_NAME, data.userFirstName);
    if (data.userLastName) localStorage.setItem(STORAGE_KEYS.USER_LAST_NAME, data.userLastName);
    if (data.userRole) localStorage.setItem(STORAGE_KEYS.USER_ROLE, data.userRole);

    // Full name
    const fullName = [data.userFirstName, data.userMiddleName, data.userLastName]
      .filter(Boolean)
      .join(' ');
    if (fullName) localStorage.setItem(STORAGE_KEYS.FULL_NAME, fullName);

    // Permissions
    if (data.permissions) {
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(data.permissions));

      // Scope configuration
      if (data.permissions.scopeConfiguration) {
        localStorage.setItem(
          STORAGE_KEYS.SCOPE_CONFIG,
          JSON.stringify(data.permissions.scopeConfiguration)
        );

        // Auto-select primary entity
        const scope = data.permissions.scopeConfiguration;
        const primaryEntity = scope.accessibleEntities.find((e) => e.isPrimary)
          || scope.accessibleEntities[0];
        if (primaryEntity) {
          localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_ID, primaryEntity.id);
          localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_NAME, primaryEntity.entityName);
        }

        // Auto-select primary business unit
        const primaryBU = scope.accessibleBusinessUnits.find((b) => b.isPrimary)
          || scope.accessibleBusinessUnits[0];
        if (primaryBU) {
          localStorage.setItem(STORAGE_KEYS.SELECTED_BU_ID, primaryBU.id);
          localStorage.setItem(STORAGE_KEYS.SELECTED_BU_NAME, primaryBU.buName);
        }

        // Auto-select current/primary financial year
        const currentFY = scope.accessibleFinancialYears.find((f) => f.isPrimary)
          || scope.accessibleFinancialYears.find((f) => f.isCurrent)
          || scope.accessibleFinancialYears[0];
        if (currentFY) {
          localStorage.setItem(STORAGE_KEYS.SELECTED_FY_ID, currentFY.id);
          localStorage.setItem(STORAGE_KEYS.SELECTED_FY_NAME, currentFY.fyName);
          localStorage.setItem(STORAGE_KEYS.SELECTED_FY_CODE, currentFY.fyCode);
        }
      }
    }
  }

  // ============================================================================
  // USER MANAGEMENT METHODS (Admin) — unchanged
  // ============================================================================

  async getUsers(params?: any): Promise<ListResponse<User>> {
    const endpoint = params
      ? buildEndpointWithQuery(userEndpoints.list(), params)
      : userEndpoints.list();

    const response = await get<ListResponse<User>>(endpoint);
    return response.data;
  }

  async getUserById(id: ID): Promise<ApiResponse<User>> {
    const response = await get<ApiResponse<User>>(userEndpoints.getById(id));
    return response.data;
  }

  async createUser(data: RegisterRequest): Promise<ApiResponse<User>> {
    const response = await post<ApiResponse<User>>(userEndpoints.create(), data);
    return response.data;
  }

  async updateUser(id: ID, data: Partial<User>): Promise<ApiResponse<User>> {
    const response = await put<ApiResponse<User>>(userEndpoints.update(id), data);
    return response.data;
  }

  async deleteUser(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(userEndpoints.delete(id));
    return response.data;
  }

  async searchUsers(params: any): Promise<ListResponse<User>> {
    const response = await post<ListResponse<User>>(userEndpoints.search(), params);
    return response.data;
  }

  async getActiveUsers(): Promise<ListResponse<User>> {
    const response = await get<ListResponse<User>>(userEndpoints.active());
    return response.data;
  }

  async updateUserStatus(id: ID, isActive: boolean): Promise<EmptyResponse> {
    const response = await patch<EmptyResponse>(userEndpoints.updateStatus(id), {
      isActive,
    });
    return response.data;
  }

  async assignRole(id: ID, role: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(userEndpoints.assignRole(id), { role });
    return response.data;
  }

  async assignPermissions(id: ID, permissions: string[]): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(userEndpoints.assignPermissions(id), {
      permissions,
    });
    return response.data;
  }

  async bulkDeleteUsers(ids: ID[]): Promise<BulkOperationResponse> {
    const response = await del<BulkOperationResponse>(userEndpoints.bulkDelete(), {
      data: { ids },
    });
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const authService = new AuthService();
export default authService;
